/**
 * Dealer's Choice Types
 *
 * Types for the gamified challenge discovery game.
 * Based on Doc 4: Explore & Discovery and Doc 8: Gamification System
 */

import type { BaseEntity, UUID } from './common.types'

/**
 * Familiarity zones for card types
 */
export type FamiliarityZone = 'comfort_push' | 'rarely_tried' | 'uncharted'

/**
 * Zone display configuration
 */
export const ZONE_CONFIG: Record<FamiliarityZone, { label: string; color: string; multiplier: number }> = {
  comfort_push: { label: 'Comfort Push', color: 'green', multiplier: 1.5 },
  rarely_tried: { label: 'Rarely Tried', color: 'orange', multiplier: 1.75 },
  uncharted: { label: 'Uncharted', color: 'purple', multiplier: 2.0 },
}

/**
 * Dealer's Choice card
 */
export interface DealersChoiceCard {
  challengeId: UUID
  challengeTitle: string
  challengeCategory: string
  challengeDifficulty: 'beginner' | 'intermediate' | 'advanced'
  challengeImageUrl?: string
  zone: FamiliarityZone
  multiplier: number
  position: number // 1, 2, or 3
}

/**
 * Game status
 */
export type DCGameStatus = 'dealing' | 'revealed' | 'accepted' | 'passed' | 'abandoned'

/**
 * Dealer's Choice game round
 */
export interface DealersChoiceGame extends BaseEntity {
  userId: UUID
  cards: DealersChoiceCard[]
  selectedCardIndex?: number
  status: DCGameStatus
  cooldownEndsAt?: string
  xpEarned?: number
}

/**
 * DC history entry
 */
export interface DCHistoryEntry extends BaseEntity {
  userId: UUID
  challengeTitle: string
  zone: FamiliarityZone
  multiplier: number
  status: 'accepted' | 'passed' | 'abandoned'
  xpEarned?: number
}

/**
 * Group DC session
 */
export interface GroupDealersChoice extends BaseEntity {
  createdBy: UUID
  communityId: UUID
  communityName?: string
  participants: UUID[]
  cards: DealersChoiceCard[]
  votes: Record<UUID, number> // userId -> card index
  status: 'voting' | 'decided' | 'expired'
  decidedCardIndex?: number
}

/**
 * Cooldown duration in hours
 */
export const DC_COOLDOWN_HOURS = 24
